import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'

import { ApiError, api } from '../../lib/api'
import type { AvailabilitySettings, Blackout, Rule } from '../../lib/optypes'
import { WEEKDAYS, shortDateTime } from '../../lib/optypes'
import { useOperatorTimezone } from '../../lib/operator'

import { OperatorLayout } from './OperatorLayout'

const DEFAULT_WINDOW = { start: '07:00', end: '19:00' }

/**
 * Availability (01 §4, D24).
 *
 * The weekly template is edited as a whole and saved as a whole. That mirrors how the API
 * stores it and, more importantly, how a person thinks about a week — "what does my week look
 * like" should be one glance, not a reconstruction from a list of edits.
 *
 * Editing this can never disturb a booked visit: `open_slots()` subtracts live bookings
 * regardless of the rules, so a visit survives Andrew deleting the window it came from. The
 * screen says so, because that is not obvious and the fear of breaking something is what stops
 * people keeping a calendar up to date.
 */
export function Availability() {
  const queryClient = useQueryClient()
  const tz = useOperatorTimezone()
  const [rows, setRows] = useState<Rule[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const rules = useQuery({
    queryKey: ['op-rules'],
    queryFn: () => api.get<Rule[]>('/op/availability/rules'),
  })
  const settings = useQuery({
    queryKey: ['op-av-settings'],
    queryFn: () => api.get<AvailabilitySettings>('/op/availability/settings'),
  })
  const blackouts = useQuery({
    queryKey: ['op-blackouts'],
    queryFn: () => api.get<Blackout[]>('/op/blackouts'),
  })

  useEffect(() => {
    if (rules.data && rows === null) setRows(rules.data)
  }, [rules.data, rows])

  const saveRules = useMutation({
    mutationFn: (next: Rule[]) =>
      api.put('/op/availability/rules', {
        rules: next.map(({ id: _id, ...rest }) => ({
          ...rest,
          start_time: rest.start_time.length === 5 ? `${rest.start_time}:00` : rest.start_time,
          end_time: rest.end_time.length === 5 ? `${rest.end_time}:00` : rest.end_time,
        })),
      }),
    onSuccess: () => {
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['op-rules'] })
    },
    onError: (err) =>
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Could not save.',
      ),
  })

  const saveSettings = useMutation({
    mutationFn: (body: AvailabilitySettings) => api.put('/op/availability/settings', body),
    onSuccess: () => {
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['op-av-settings'] })
    },
    onError: (err) =>
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Could not save.',
      ),
  })

  const addBlackout = useMutation({
    mutationFn: (body: { start_ts: string; end_ts: string; reason: string | null }) =>
      api.post('/op/blackouts', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['op-blackouts'] }),
    onError: (err) =>
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Could not save.',
      ),
  })

  const removeBlackout = useMutation({
    mutationFn: (id: string) => api.delete(`/op/blackouts/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['op-blackouts'] }),
  })

  function forDay(weekday: number): Rule | undefined {
    return rows?.find((r) => r.weekday === weekday)
  }

  function toggleDay(weekday: number, on: boolean) {
    setRows((current) => {
      const rest = (current ?? []).filter((r) => r.weekday !== weekday)
      if (!on) return rest
      return [
        ...rest,
        {
          weekday,
          start_time: DEFAULT_WINDOW.start,
          end_time: DEFAULT_WINDOW.end,
          visit_minutes: 60,
          buffer_minutes: 30,
          enabled: true,
        },
      ].sort((a, b) => a.weekday - b.weekday)
    })
  }

  function editDay(weekday: number, patch: Partial<Rule>) {
    setRows((current) =>
      (current ?? []).map((r) => (r.weekday === weekday ? { ...r, ...patch } : r)),
    )
  }

  function onAddBlackout(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const from = String(form.get('from') ?? '')
    const to = String(form.get('to') ?? '')
    if (!from || !to) return
    addBlackout.mutate({
      start_ts: new Date(from).toISOString(),
      end_ts: new Date(to).toISOString(),
      reason: (form.get('reason') as string) || null,
    })
    event.currentTarget.reset()
  }

  return (
    <OperatorLayout>
      <h1 className="text-2xl">Availability</h1>
      <p className="mt-2 text-sm text-muted">
        Homeowners only ever see times that survive all of this. Changing your week never moves or
        cancels a visit that is already booked.
      </p>

      {error && (
        <p role="alert" className="mt-4 rounded-btn bg-blue-tint px-3.5 py-2.5 text-sm text-ink">
          {error}
        </p>
      )}

      <section className="mt-6 rounded-card border border-line bg-white p-5">
        <h2 className="text-lg">Your week</h2>
        <ul className="m-0 mt-4 grid list-none gap-2.5 p-0">
          {WEEKDAYS.map((name, weekday) => {
            const rule = forDay(weekday)
            return (
              <li
                key={name}
                className="grid gap-3 rounded-btn border border-line p-3.5 sm:grid-cols-[9rem_1fr] sm:items-center sm:gap-4"
              >
                <label className="flex items-center gap-2.5 font-semibold text-ink">
                  <input
                    type="checkbox"
                    checked={Boolean(rule)}
                    onChange={(e) => toggleDay(weekday, e.target.checked)}
                    className="size-[18px] accent-[var(--blue)]"
                  />
                  {/* The id is what makes each field below announce as "Monday Start" rather
                      than as the seventh control called "Start" on the page. */}
                  <span id={`day-${weekday}`}>{name}</span>
                </label>
                {rule ? (
                  // Two columns on a phone, four across from `sm`. The previous version was a
                  // wrapping row of bare inputs joined by the words "to", "visit" and
                  // "+ buffer", which read fine at 1280 px and broke into orphaned labels on a
                  // phone: "7:00 AM to" then a newline. Every field carries its own label now.
                  <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                    <TimeField
                      dayId={`day-${weekday}`}
                      id={`${name}-start`}
                      label="Start"
                      value={rule.start_time.slice(0, 5)}
                      onChange={(value) => editDay(weekday, { start_time: value })}
                    />
                    <TimeField
                      dayId={`day-${weekday}`}
                      id={`${name}-end`}
                      label="End"
                      value={rule.end_time.slice(0, 5)}
                      onChange={(value) => editDay(weekday, { end_time: value })}
                    />
                    <DayField
                      dayId={`day-${weekday}`}
                      id={`${name}-visit`}
                      label="Visit"
                      suffix="min"
                      type="number"
                      min={15}
                      max={480}
                      step={15}
                      value={String(rule.visit_minutes)}
                      onChange={(value) => editDay(weekday, { visit_minutes: Number(value) })}
                    />
                    <DayField
                      dayId={`day-${weekday}`}
                      id={`${name}-buffer`}
                      label="Buffer"
                      suffix="min"
                      type="number"
                      min={0}
                      max={240}
                      step={15}
                      value={String(rule.buffer_minutes)}
                      onChange={(value) => editDay(weekday, { buffer_minutes: Number(value) })}
                    />
                  </div>
                ) : (
                  <span className="text-sm text-muted">Not available</span>
                )}
              </li>
            )
          })}
        </ul>
        <button
          type="button"
          disabled={saveRules.isPending || rows === null}
          onClick={() => rows && saveRules.mutate(rows)}
          className="mt-4 rounded-btn bg-blue px-6 py-3 font-bold text-white disabled:opacity-60"
        >
          {saveRules.isPending ? 'Saving…' : 'Save my week'}
        </button>
      </section>

      {settings.data && (
        <section className="mt-4 rounded-card border border-line bg-white p-5">
          <h2 className="text-lg">Rules of thumb</h2>
          <form
            className="mt-4 grid gap-4 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault()
              const form = new FormData(event.currentTarget)
              saveSettings.mutate({
                lead_time_hours: Number(form.get('lead_time_hours')),
                max_horizon_days: Number(form.get('max_horizon_days')),
                hold_minutes: Number(form.get('hold_minutes')),
                timezone: settings.data.timezone,
              })
            }}
          >
            <NumberField
              name="lead_time_hours"
              label="Shortest notice (hours)"
              defaultValue={settings.data.lead_time_hours}
            />
            <NumberField
              name="max_horizon_days"
              label="Book up to (days ahead)"
              defaultValue={settings.data.max_horizon_days}
            />
            <NumberField
              name="hold_minutes"
              label="Hold a slot for (minutes)"
              defaultValue={settings.data.hold_minutes}
            />
            <button
              type="submit"
              disabled={saveSettings.isPending}
              className="rounded-btn bg-blue px-6 py-3 font-bold text-white disabled:opacity-60 sm:col-span-3 sm:justify-self-start"
            >
              {saveSettings.isPending ? 'Saving…' : 'Save'}
            </button>
          </form>
        </section>
      )}

      <section className="mt-4 rounded-card border border-line bg-white p-5">
        <h2 className="text-lg">Days off</h2>
        <p className="mt-1.5 text-sm text-muted">
          Holidays, other jobs, anything. These win over your weekly hours.
        </p>

        <form onSubmit={onAddBlackout} className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <LabelledInput name="from" label="From" type="datetime-local" required />
          <LabelledInput name="to" label="To" type="datetime-local" required />
          <LabelledInput name="reason" label="Why (optional)" type="text" />
          <button
            type="submit"
            disabled={addBlackout.isPending}
            className="self-end rounded-btn border border-line-2 bg-white px-5 py-2.5 font-semibold text-ink disabled:opacity-60"
          >
            Add
          </button>
        </form>

        <ul className="m-0 mt-4 grid list-none gap-2 p-0">
          {blackouts.data?.length === 0 && <li className="text-sm text-muted">None coming up.</li>}
          {blackouts.data?.map((blackout) => (
            <li
              key={blackout.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-btn border border-line px-3.5 py-2.5 text-sm"
            >
              <span className="text-ink">
                {shortDateTime(blackout.start_ts, tz)} → {shortDateTime(blackout.end_ts, tz)}
                {blackout.reason && <span className="ml-2 text-muted">{blackout.reason}</span>}
              </span>
              <button
                type="button"
                onClick={() => removeBlackout.mutate(blackout.id)}
                className="font-semibold text-blue-deep underline-offset-4 hover:underline"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      </section>
    </OperatorLayout>
  )
}

/**
 * One labelled control inside a weekday row.
 *
 * The label is visible rather than an `aria-label`: at 393 px there is no surrounding prose to
 * infer "the second box is the end time" from, and a sighted person needs the same help a
 * screen-reader user was already getting.
 */
/** Every quarter hour of the day, as `["07:00", "7:00 AM"]`. Built once. */
const QUARTER_HOURS: [string, string][] = Array.from({ length: 96 }, (_, i) => {
  const hours = Math.floor(i / 4)
  const minutes = (i % 4) * 15
  const value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  const hour12 = hours % 12 === 0 ? 12 : hours % 12
  return [value, `${hour12}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`]
})

/**
 * Start and end of a working day, as a list rather than `<input type="time">`.
 *
 * The native control is three different controls: Chrome draws a text field with a clock
 * button, Firefox draws a plain field, and WebKit draws a spinner that shows seconds — so
 * "07:00" reads back as "7:00:00 AM" on a phone no matter what `step` says. A select renders
 * as a wheel on iOS, a list everywhere else, has one appearance to design for, and cannot
 * produce a time that is not on the quarter hour the rest of this screen works in.
 */
function TimeField({
  id,
  dayId,
  label,
  value,
  onChange,
}: {
  id: string
  dayId: string
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="grid gap-1">
      <label
        id={`${id}-label`}
        htmlFor={id}
        className="font-mono text-xs uppercase tracking-[0.1em] text-muted"
      >
        {label}
      </label>
      <select
        id={id}
        aria-labelledby={`${dayId} ${id}-label`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        // Tighter at 320 px, where the native dropdown arrow plus 10 px of padding clipped
        // "7:00 AM" to "7:00 AI".
        className="h-11 w-full rounded-btn border border-line-2 bg-white px-2 text-sm text-ink min-[360px]:px-2.5 min-[360px]:text-[15px]"
      >
        {/* A saved value off the quarter hour (edited before this control existed, or by the
            API) would otherwise silently snap to the first option on the next save. */}
        {!QUARTER_HOURS.some(([option]) => option === value) && (
          <option value={value}>{value}</option>
        )}
        {QUARTER_HOURS.map(([option, display]) => (
          <option key={option} value={option}>
            {display}
          </option>
        ))}
      </select>
    </div>
  )
}

function DayField({
  id,
  dayId,
  label,
  suffix,
  type,
  value,
  onChange,
  min,
  max,
  step,
}: {
  id: string
  dayId: string
  label: string
  suffix?: string
  type: 'number'
  value: string
  onChange: (value: string) => void
  min?: number
  max?: number
  step?: number
}) {
  return (
    <div className="grid gap-1">
      <label
        id={`${id}-label`}
        htmlFor={id}
        className="font-mono text-xs uppercase tracking-[0.1em] text-muted"
      >
        {label}
        {suffix && <span className="normal-case tracking-normal"> ({suffix})</span>}
      </label>
      <input
        id={id}
        aria-labelledby={`${dayId} ${id}-label`}
        type={type}
        inputMode={type === 'number' ? 'numeric' : undefined}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full rounded-btn border border-line-2 bg-white px-2.5 text-[15px] text-ink"
      />
    </div>
  )
}

function NumberField({
  name,
  label,
  defaultValue,
}: {
  name: string
  label: string
  defaultValue: number
}) {
  return (
    <div className="grid gap-1.5">
      <label htmlFor={name} className="text-sm font-semibold text-ink">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type="number"
        defaultValue={defaultValue}
        className="h-11 w-full rounded-btn border border-line-2 bg-white px-3.5"
      />
    </div>
  )
}

function LabelledInput({
  name,
  label,
  type,
  required,
}: {
  name: string
  label: string
  type: string
  required?: boolean
}) {
  return (
    <div className="grid gap-1.5">
      <label htmlFor={name} className="text-sm font-semibold text-ink">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        className="h-11 w-full rounded-btn border border-line-2 bg-white px-3.5"
      />
    </div>
  )
}
